import {
  Activity,
  Wifi,
  Database,
  HardDrive,
  RefreshCw,
  Heart,
  Shield,
  Waves,
} from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTheme } from "../context/ThemeContext";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import { Spin } from "antd";
import InfoTooltip from "./ui/InfoTooltip";

export function SystemHealth({ onClick, data, loading }) {
  const { systemHealth, systemData, systemDevice, last, next } = data || {};
  const { theme, colors } = useTheme();

  useEffect(() => {
    console.log("System Health Data:", data);
  }, [data]);

  const healthValue = Number(systemHealth) || 0;

  let healthStatus = "Healthy";
  let healthColor = "#2b9f7a"; // green
  let healthGradientFrom = "#2b9f7a";
  let healthGradientTo = "#228385";

  if (healthValue <= 60) {
    healthStatus = "UnHealthy";
    healthColor = "#ef4444"; // red
    healthGradientFrom = "#ef4444";
    healthGradientTo = "#b91c1c";
  } else if (healthValue > 60 && healthValue <= 80) {
    healthStatus = "Alarming";
    healthColor = "#f97316"; // orange
    healthGradientFrom = "#f97316";
    healthGradientTo = "#ea580c";
  }

  const metrics = [
    {
      label: "Communication Status",
      icon: Heart,
      value: `${Math.min(systemHealth ?? 0, 100).toFixed(0)}%`,
      status: systemHealth > 90 ? "Excellent" : "Warning",
      color:
        systemHealth >= 80
          ? "#32cd32"
          : systemHealth >= 60
            ? "#ff8c00"
            : "#d13838", // Green for 80+, Orange for 60-80, Red for below 60
      tab: "communicationStatus",
      info: "Shows the overall communication status of meters based on last communication time and encoder communication errors.",
    },
    {
      label: "Data Status",
      icon: Database,
      value: `${Math.min(systemData ?? 0, 100).toFixed(0)}%`,
      status: systemData > 95 ? "Active" : "Warning",
      color:
        systemData >= 80 ? "#32cd32" : systemData >= 60 ? "#ff8c00" : "#d13838", // Green for 80+, Orange for 60-80, Red for below 60
      tab: "dataStatus",
      info: "Determined by the number of outliers detected in meter flow data over the last two weeks.",
    },
    {
      label: "Device Status",
      icon: HardDrive,
      value: `${Math.min(systemDevice ?? 0, 100).toFixed(0)}%`,
      status: systemDevice > 90 ? "Healthy" : "Warning",
      color:
        systemDevice >= 80
          ? "#32cd32"
          : systemDevice >= 60
            ? "#ff8c00"
            : "#d13838", // Green for 80+, Orange for 60-80, Red for below 60
      tab: "deviceStatus",
      info: "Shows the overall device status of meters based on endpoint tamper events, battery level, and signal strength.",
    },
  ];

  return (
    <motion.div
      className="rounded-xl p-6 border-l-[0.5px]  relative overflow-hidden h-full flex flex-col group transition-colors duration-300"
      style={{
        backgroundColor: theme === "white" ? "#ffffff" : colors.glassEffect,
        // backgroundColor: colors.cardBg,
        backdropFilter: "blur(10px)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: theme === "white" ? colors.border : `${colors.primary}15`,
      }}
      initial={{ opacity: 0, x: -50, rotateZ: -10 }}
      animate={{ opacity: 1, x: 0, rotateZ: 0 }}
      transition={{ duration: 0.8, delay: 0.1, type: "spring", bounce: 0.4 }}
      whileHover={{
        x: 10,
        transition: { type: "spring", stiffness: 300 },
      }}
      onClick={onClick}
    >
      {/* Animated top line on hover */}
      <div
        className="absolute top-0 left-0 h-0.5 w-0 group-hover:w-full transition-all duration-400 ease-out rounded-full"
        style={{
          backgroundImage: colors.gradient,
        }}
      />

      <motion.div
        className="absolute top-0 right-0 w-72 h-72 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle, ${colors.primary}20, transparent 70%)`,
        }}
      />

      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <div
            className="p-2 rounded-lg"
            style={{ backgroundImage: colors.gradient }}
          >
            <Waves className="w-5 h-5 text-white" />
          </div>
          <h3
            className="text-2xl font-bold tracking-tight"
            style={{ color: colors.text }}
          >
            System Health
          </h3>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Spin size="large" />
          </div>
        ) : (
          <>
            {/* Pulsing Heart */}
            <div className="flex items-center justify-center mb-4">
              <motion.div
                className="relative"
                animate={{ scale: [1, 1.15, 1] }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <svg
                  width="180"
                  height="180"
                  viewBox="0 0 24 24"
                  className="w-44 h-44"
                >
                  <defs>
                    <linearGradient
                      id="heartGradientTop"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor={healthGradientFrom} />
                      <stop offset="100%" stopColor={healthGradientTo} />
                    </linearGradient>

                    <linearGradient
                      id="heartGradientMiddle"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor={healthGradientFrom} />
                      <stop offset="100%" stopColor={healthGradientTo} />
                    </linearGradient>

                    <linearGradient
                      id="heartGradientBottom"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor={healthGradientFrom} />
                      <stop offset="100%" stopColor={healthGradientTo} />
                    </linearGradient>

                    <filter
                      id="heartShadow"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feGaussianBlur in="SourceAlpha" stdDeviation="0.4" />
                      <feOffset dx="0" dy="0.5" result="offsetblur" />
                      <feComponentTransfer>
                        <feFuncA type="linear" slope="0.5" />
                      </feComponentTransfer>
                      <feMerge>
                        <feMergeNode />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {/* Bottom */}
                  <motion.path
                    d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                    fill="url(#heartGradientBottom)"
                    transform="translate(0.6, 0.6)"
                    opacity="0.5"
                    animate={{ scale: [1, 1.02, 1] }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    style={{ transformOrigin: "center" }}
                  />

                  {/* Middle */}
                  <motion.path
                    d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                    fill="url(#heartGradientMiddle)"
                    transform="translate(0.3, 0.3)"
                    filter="url(#heartShadow)"
                    animate={{ scale: [1, 1.03, 1] }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    style={{ transformOrigin: "center" }}
                  />

                  {/* Top */}
                  <motion.path
                    d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                    fill="url(#heartGradientTop)"
                    stroke={healthGradientFrom}
                    strokeWidth="0.3"
                    filter="url(#heartShadow)"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    style={{ transformOrigin: "center" }}
                  />
                </svg>
              </motion.div>
            </div>
            <motion.div
              className="flex items-center justify-center gap-2 mb-6"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <motion.div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: healthColor }}
                animate={{
                  scale: [1, 1.3, 1],
                  boxShadow: [
                    `0 0 0px ${healthColor}80`,
                    `0 0 10px ${healthColor}`,
                    `0 0 0px ${healthColor}80`,
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              <span
                className="text-transparent bg-clip-text text-sm font-bold tracking-wide"
                style={{
                  backgroundImage: `linear-gradient(to right, ${healthGradientFrom}, ${healthGradientTo})`,
                  WebkitTextFillColor: "transparent",
                  WebkitBackgroundClip: "text",
                }}
              >
                Status: {healthStatus}
              </span>
            </motion.div>

            {/* Metrics */}
            <div className="grid grid-cols-3 max-[1650px]:grid-cols-2 gap-4 mb-6">
              {metrics.map((metric, index) => {
                const Icon = metric.icon;
                return (
                  <motion.div
                    key={index}
                    className="relative rounded-lg p-4 border transition-colors cursor-pointer h-full"
                    style={{
                      backgroundColor: colors.background,
                      borderColor: colors.border,
                      height: '100%'
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1 + index * 0.1 }}
                    whileHover={{ y: -3 }}
                    onClick={(e) => {
                      if (e.target.closest("button")) return;
                      if (e.target.closest(".fixed")) return;

                      e.stopPropagation();
                      onClick?.(metric.tab);
                    }}
                  >
                    <div className="relative flex flex-col items-center text-center">
                      <p
                        className="text-2xl font-bold mb-2"
                        style={{ color: metric.color }}
                      >
                        {metric.value}
                      </p>
                      <div className="flex items-center justify-center relative">
                        <p
                          className="text-xs font-medium"
                          style={{ color: colors.textSecondary }}
                        >
                          {metric.label}
                        </p>
                        {/* <MetricInfo text={metric.info} /> */}
                        {metric.info && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <InfoTooltip text={metric.info} />
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            <div
              className="py-6 border-t"
              style={{ borderColor: colors.border }}
            >
              {/* Two cards below */}
              <div className="grid grid-cols-2 gap-3">
                <motion.div
                  className="backdrop-blur-sm rounded-lg p-3 border transition-colors"
                  style={{
                    backgroundColor: `${colors.primary}10`,
                    borderColor: `${colors.primary}20`,
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <span
                    className="text-xs block mb-1"
                    style={{ color: colors.textSecondary }}
                  >
                    Last Updated
                  </span>
                  <span
                    className="text-sm font-medium"
                    style={{ color: colors.text }}
                  >
                    {last || "-"}
                  </span>
                </motion.div>

                <motion.div
                  className="backdrop-blur-sm rounded-lg p-3 border transition-colors"
                  style={{
                    backgroundColor: `${colors.primary}10`,
                    borderColor: `${colors.primary}20`,
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <span
                    className="text-xs block mb-1"
                    style={{ color: colors.textSecondary }}
                  >
                    Next Update
                  </span>
                  <span
                    className="text-sm font-medium"
                    style={{ color: colors.text }}
                  >
                    {next || "-"}
                  </span>
                </motion.div>
              </div>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
